// mockCharacters.js
// -----------------------------------------------------------------------------
// Fixtures for the four worked-example characters from the design doc:
//   - Diana (range-y supporting, grouchy/frustrated/teasing)
//   - Cambitter Dorelli (lead, strategic + supportive co-dominant)
//   - Gate (single-affect minor, informative)
//   - Bystander Three (thin-card walk-on)
//
// Each entry mirrors what `VariationResolverService.fetchCharacterCorpusAffects`
// + `SpeakerContextResolverService.fetchSpeakerContext` + Query G would
// produce, but as a pre-rendered fixture. Sentence-template outputs are
// pre-computed too so the mockup doesn't drag the template engine in yet.
//
// Spec reference: PHASE_4D_VA_OPPORTUNITY_CHARACTER_HOVER_PANEL_DESIGN.md §3.1
//
// `chapterPresence[i].sampleLines` is a per-chapter fixture for the
// chapter-click → dialogue-popup feature (added 2026-05-17). Each entry is
// `{ line, toName }` where `line` is the verbatim dialogue text (without
// surrounding quotes — the popup adds them) and `toName` is the
// interlocutor's display name for the small "— to {name}" attribution line.
// In production these would come from a per-chapter slice of
// `:DialogueLine.content` joined to the speaker-context interlocutor
// resolution.
// -----------------------------------------------------------------------------

const mockCharacters = [
  // ---------------------------------------------------------------------------
  // Diana — range-y supporting character, frustrated-dominant with teasing &
  // grouchy in the rotation. Demonstrates a 3-affect distribution that lets the
  // "Mostly opposite X and Y" speaker-context template fire.
  // ---------------------------------------------------------------------------
  {
    id: 'diana',
    name: 'Diana',
    roleSize: 'supporting role',
    lineCount: 89,
    chapterCount: 8,
    bookTotalChapters: 14,

    headlineNL: 'Diana shows frustration a lot, and rarely apologizes.',
    speakerContextSentence: 'Mostly opposite Cambitter and Gate. Occasional scenes with Diogenes.',
    presenceSentence: 'Appears in chapters 3–11, heaviest in 7–9.',

    voiceExcerpt: {
      emotion: 'frustrated',
      content:
        '"I have told you twice already, and I shall not tell you again — the matter is closed."',
    },

    dominantAffects: [
      { emotion: 'frustrated', charCount: 525, share: 0.52 },
      { emotion: 'teasing', charCount: 282, share: 0.28 },
      { emotion: 'grouchy', charCount: 195, share: 0.20 },
    ],

    speakerContext: [
      { name: 'Cambitter', count: 22, share: 0.42 },
      { name: 'Gate', count: 14, share: 0.27 },
      { name: 'Diogenes', count: 8, share: 0.15 },
    ],

    chapterPresence: [
      {
        chapter: 3,
        lineCount: 4,
        sampleLines: [
          { line: 'Must we discuss this again?', toName: 'Cambitter' },
          { line: 'Not at this hour, surely.', toName: 'Cambitter' },
        ],
      },
      {
        chapter: 4,
        lineCount: 8,
        sampleLines: [
          { line: "You'd think a man your age would know better.", toName: 'Cambitter' },
          { line: 'And yet here we are.', toName: 'Gate' },
        ],
      },
      {
        chapter: 5,
        lineCount: 12,
        sampleLines: [
          { line: "Spare me the speech, I've heard every word of it before.", toName: 'Cambitter' },
          { line: 'Twice, in fact. Possibly three times.', toName: 'Cambitter' },
          { line: 'No, I am perfectly well, thank you for not asking.', toName: 'Gate' },
        ],
      },
      {
        chapter: 6,
        lineCount: 9,
        sampleLines: [
          { line: 'Oh, marvellous — another lecture from the great strategist.', toName: 'Cambitter' },
          { line: "Don't look at me like that, Gate, you started it.", toName: 'Gate' },
        ],
      },
      {
        chapter: 7,
        lineCount: 18,
        sampleLines: [
          { line: 'I have told you twice already, and I shall not tell you again — the matter is closed.', toName: 'Cambitter' },
          { line: "And do not pretend you didn't hear me the first time.", toName: 'Cambitter' },
          { line: 'You — yes, you, Diogenes — wipe that look off your face.', toName: 'Diogenes' },
        ],
      },
      {
        chapter: 8,
        lineCount: 16,
        sampleLines: [
          { line: 'If you all insist on being children about this, I shall leave the room.', toName: 'Cambitter' },
          { line: 'Try me. Go on.', toName: 'Cambitter' },
          { line: 'You may keep your apology, thank you, I have no use for it.', toName: 'Diogenes' },
        ],
      },
      {
        chapter: 9,
        lineCount: 14,
        sampleLines: [
          { line: 'There. Was that so difficult?', toName: 'Cambitter' },
          { line: 'No, no, do go on. I find I am enjoying myself.', toName: 'Gate' },
        ],
      },
      {
        chapter: 10,
        lineCount: 5,
        sampleLines: [
          { line: "I'll not say another word until you sit down.", toName: 'Cambitter' },
        ],
      },
      {
        chapter: 11,
        lineCount: 3,
        sampleLines: [
          { line: 'Whatever the two of you have decided — fine. Fine.', toName: 'Cambitter' },
        ],
      },
    ],

    bcNote: null,
    isThinCard: false,
  },

  // ---------------------------------------------------------------------------
  // Cambitter Dorelli — lead, strategic-dominant with a supportive secondary
  // that qualifies as co-dominant (>= 0.5 ratio, >= 100 chars). Demonstrates the
  // dual-tone accent override and the long-sentence formal-strategic variant.
  // ---------------------------------------------------------------------------
  {
    id: 'cambitter',
    name: 'Cambitter Dorelli',
    roleSize: 'lead',
    lineCount: 142,
    chapterCount: 11,
    bookTotalChapters: 14,

    headlineNL:
      'Cambitter is mostly strategic — patient, calculating, with rare warmth.',
    speakerContextSentence: 'Mostly opposite Diana and Eldest. Recurring scenes with Gate.',
    presenceSentence: 'Appears throughout, heaviest in chapters 4–10.',

    voiceExcerpt: {
      emotion: 'strategic',
      content:
        '"If we were to consider this carefully, you and I, I should think we might find that the right path lies not in confrontation but in the quiet preparation of certain inevitabilities … I should think …"',
    },

    dominantAffects: [
      { emotion: 'strategic', charCount: 880, share: 0.55 },
      { emotion: 'supportive', charCount: 320, share: 0.20 },
      { emotion: 'testy', charCount: 160, share: 0.10 },
      { emotion: 'apologetic', charCount: 80, share: 0.05 },
    ],

    speakerContext: [
      { name: 'Diana', count: 28, share: 0.34 },
      { name: 'Eldest', count: 22, share: 0.27 },
      { name: 'Gate', count: 18, share: 0.22 },
    ],

    chapterPresence: [
      {
        chapter: 1,
        lineCount: 6,
        sampleLines: [
          { line: 'I find that a quiet entrance serves a purpose more often than a loud one.', toName: 'Eldest' },
          { line: 'Let us not, however, mistake quiet for absent.', toName: 'Eldest' },
        ],
      },
      {
        chapter: 2,
        lineCount: 8,
        sampleLines: [
          { line: 'You may consider me, if it eases the conversation, a careful observer.', toName: 'Diana' },
          { line: 'I have found careful observation to be the rarest of the inexpensive virtues.', toName: 'Diana' },
        ],
      },
      {
        chapter: 3,
        lineCount: 10,
        sampleLines: [
          { line: 'If we proceed as Diana suggests, we lose the option of waiting. If we wait, we keep both.', toName: 'Eldest' },
          { line: 'I would, in your position, choose to keep both — for the moment.', toName: 'Eldest' },
        ],
      },
      {
        chapter: 4,
        lineCount: 16,
        sampleLines: [
          { line: 'Diana, I have heard your objection, and I find some merit in it — but not all.', toName: 'Diana' },
          { line: 'The merit is here; the missing part is the question of timing.', toName: 'Diana' },
          { line: "Gate — your reading, if you'd be so kind.", toName: 'Gate' },
        ],
      },
      {
        chapter: 5,
        lineCount: 18,
        sampleLines: [
          { line: 'If we were to consider this carefully, you and I, I should think we might find that the right path lies not in confrontation but in the quiet preparation of certain inevitabilities … I should think …', toName: 'Diana' },
          { line: 'And in the meantime — let them believe what they prefer to believe.', toName: 'Diana' },
        ],
      },
      {
        chapter: 6,
        lineCount: 14,
        sampleLines: [
          { line: "There is no need to apologize. None of us could have foreseen — none of us did.", toName: 'Diana' },
          { line: 'Sit, please. We have time still.', toName: 'Diana' },
        ],
      },
      {
        chapter: 7,
        lineCount: 19,
        sampleLines: [
          { line: 'The matter is, with respect, very far from closed.', toName: 'Diana' },
          { line: 'I will say it gently, but I will say it. We are not finished here.', toName: 'Diana' },
          { line: 'Gate, would you take the long view a moment?', toName: 'Gate' },
        ],
      },
      {
        chapter: 8,
        lineCount: 17,
        sampleLines: [
          { line: "I should very much prefer that we did not raise our voices.", toName: 'Diana' },
          { line: 'It changes nothing in the outcome and a great deal in the comfort of the room.', toName: 'Diana' },
        ],
      },
      {
        chapter: 9,
        lineCount: 15,
        sampleLines: [
          { line: 'A small concession now spares us a larger one later. That is the entire equation.', toName: 'Eldest' },
          { line: 'I am persuaded — and, I should add, only slightly testy about it.', toName: 'Diana' },
        ],
      },
      {
        chapter: 10,
        lineCount: 12,
        sampleLines: [
          { line: 'We have arrived where we ought, by the only road that did not break us getting here.', toName: 'Eldest' },
          { line: 'Gate — well done.', toName: 'Gate' },
        ],
      },
      {
        chapter: 11,
        lineCount: 4,
        sampleLines: [
          { line: 'Quietly, please. Just for now. Quietly.', toName: 'Diana' },
        ],
      },
      {
        chapter: 12,
        lineCount: 3,
        sampleLines: [
          { line: 'And so the door, I think, is open.', toName: 'Eldest' },
        ],
      },
    ],

    bcNote:
      'Lean closer to controlled than cold. He has been trained to seem icy — a trained reader hears that difference.',
    isThinCard: false,
  },

  // ---------------------------------------------------------------------------
  // Gate — single-affect minor character, informative only. Demonstrates the
  // 100%-share path (single dominant affect, no speaker-context bar pair,
  // late-entrance presence template).
  // ---------------------------------------------------------------------------
  {
    id: 'gate',
    name: 'Gate',
    roleSize: 'minor role',
    lineCount: 14,
    chapterCount: 3,
    bookTotalChapters: 14,

    headlineNL: "Gate speaks only when there's information to deliver.",
    speakerContextSentence: 'Mostly opposite Cambitter.',
    presenceSentence: 'A late entrance — first appears in chapter 7.',

    voiceExcerpt: {
      emotion: 'informative',
      content:
        '"The path you seek lies through the second arch on the eastern wall, beyond the old gardens; you will know the door by its broken latch and the faint mark left there by those who came before."',
    },

    dominantAffects: [
      { emotion: 'informative', charCount: 420, share: 1.00 },
    ],

    speakerContext: [
      { name: 'Cambitter', count: 11, share: 0.79 },
      { name: 'Diana', count: 3, share: 0.21 },
    ],

    chapterPresence: [
      {
        chapter: 7,
        lineCount: 6,
        sampleLines: [
          { line: 'The path you seek lies through the second arch on the eastern wall, beyond the old gardens; you will know the door by its broken latch and the faint mark left there by those who came before.', toName: 'Cambitter' },
          { line: 'The wardens change at the third bell. Before then, the corridor is clear.', toName: 'Cambitter' },
        ],
      },
      {
        chapter: 9,
        lineCount: 5,
        sampleLines: [
          { line: 'Three names are still on the inner list. I will not say which until we are inside.', toName: 'Cambitter' },
          { line: 'The window at the south side is sealed. The one beside it is not.', toName: 'Diana' },
        ],
      },
      {
        chapter: 12,
        lineCount: 3,
        sampleLines: [
          { line: 'The matter is closed at the door, and the door is shut at the latch.', toName: 'Cambitter' },
        ],
      },
    ],

    bcNote: null,
    isThinCard: false,
  },

  // ---------------------------------------------------------------------------
  // Bystander Three — thin-card walk-on, no `_0` exemplar passes N=200.
  // Demonstrates the graceful collapse: header + truthful-failure headline +
  // presence sentence + neutral palette + no excerpt/affect-table/sparkline.
  // The BC note (if authored) is the lifeline.
  // ---------------------------------------------------------------------------
  {
    id: 'bystander_three',
    name: 'Bystander Three',
    roleSize: 'walk-on',
    lineCount: 4,
    chapterCount: 1,
    bookTotalChapters: 14,

    headlineNL:
      "Brief part. The script doesn't give us much to characterize from — see the BC's note.",
    speakerContextSentence: null,   // hidden — speakerContext.length === 0
    presenceSentence: 'Appears only in chapter 6.',

    voiceExcerpt: null,             // hidden — no _0 exemplar at N=200

    dominantAffects: [],            // hidden — no rows

    speakerContext: [],

    chapterPresence: [
      { chapter: 6, lineCount: 4 },
    ],

    bcNote:
      'Background voice during the market scene. Read flat and quick — they barely register.',
    isThinCard: true,
  },
];

export default mockCharacters;
